import apiClient from "./client";

const STORAGE_KEY = "transfer-now-user";

const initialUser = {
  id: "",
  name: "",
  email: "",
  company: "",
  studioName: "",
  role: "user",
  accessConfigured: false,
  pageAccess: [],
  avatar: "",
  googleConnected: false,
  googleEmail: "",
  googleProfilePicture: "",
  plan: "Starter",
  lastSync: "",
};

const readStoredUser = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? { ...initialUser, ...JSON.parse(raw) } : initialUser;
  } catch (error) {
    return initialUser;
  }
};

const persistUser = (user) => {
  if (!user?.email) {
    window.localStorage.removeItem(STORAGE_KEY);
    return initialUser;
  }

  const nextUser = { ...initialUser, ...user };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
  return nextUser;
};

const normalizeUser = (payload) => {
  const source = payload?.user || payload?.data || payload || {};

  return {
    ...initialUser,
    id: source.id || source._id || source.userId || "",
    name: source.name || source.fullName || source.username || "",
    email: source.email || "",
    company: source.company || source.organization || "",
    studioName: source.studioName || source.company || source.organization || "",
    phone: source.phone || "",
    city: source.city || "",
    role: source.role || "user",
    accessConfigured: Boolean(source.accessConfigured),
    pageAccess: Array.isArray(source.pageAccess) ? source.pageAccess : [],
    avatar: source.avatar || source.picture || source.profileImage || "",
    googleConnected: Boolean(
      source.googleConnected ||
        source.isGoogleConnected ||
        source.googleAccount?.connected ||
        source.googleEmail
    ),
    googleEmail:
      source.googleEmail || source.googleAccount?.email || source.connectedGoogleEmail || "",
    googleProfilePicture:
      source.googleProfilePicture ||
      source.googleAccount?.picture ||
      source.picture ||
      "",
    plan: source.plan || source.subscriptionPlan || "Starter",
    lastSync: source.lastSync || source.googleAccount?.lastSync || "",
  };
};

export const persistNormalizedUser = (payload) =>
  persistUser(normalizeUser(payload));

const demoFallbackEnabled = process.env.REACT_APP_ENABLE_DEMO_FALLBACK === "true";
const isFallbackEligible = (error) =>
  demoFallbackEnabled && (error?.isNetworkError || error?.status === 404 || error?.status === 501);

const isUnauthenticated = (error) => error?.status === 401;

export const getCurrentUser = async () => {
  try {
    const response = await apiClient.get("/auth/me");
    return persistUser(normalizeUser(response.data));
  } catch (error) {
    if (isUnauthenticated(error)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return initialUser;
    }

    if (isFallbackEligible(error)) {
      return readStoredUser();
    }

    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await apiClient.post("/auth/login", credentials);
    return persistUser(normalizeUser(response.data));
  } catch (error) {
    if (isFallbackEligible(error)) {
      const fallbackUser = {
        ...readStoredUser(),
        id: "usr_public_demo",
        name: credentials.email.split("@")[0].replace(/[._-]/g, " "),
        email: credentials.email,
      };

      return persistUser(fallbackUser);
    }

    throw error;
  }
};

export const signUpUser = async (payload) => {
  try {
    const response = await apiClient.post("/auth/signup", payload);
    return persistUser(normalizeUser(response.data));
  } catch (error) {
    if (isFallbackEligible(error)) {
      const fallbackUser = {
        ...readStoredUser(),
        id: "usr_public_demo",
        name: payload.name,
        email: payload.email,
        company: payload.company || payload.studioName || "",
        studioName: payload.studioName || payload.company || "",
      };

      return persistUser(fallbackUser);
    }

    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await apiClient.post("/auth/logout");
  } catch (error) {
    if (!isFallbackEligible(error)) {
      throw error;
    }
  }

  window.localStorage.removeItem(STORAGE_KEY);
  return initialUser;
};

export const connectGoogleAccount = async () => {
  try {
    const response = await apiClient.get("/google/connect");
    const payload = response.data || {};

    if (payload.url || payload.redirectUrl) {
      return {
        mode: "redirect",
        url: payload.url || payload.redirectUrl,
      };
    }

    return {
      mode: "inline",
      user: persistUser(normalizeUser(payload)),
    };
  } catch (error) {
    if (isFallbackEligible(error)) {
      const fallbackUser = {
        ...readStoredUser(),
        googleConnected: true,
        googleEmail: readStoredUser().email || "demo.workspace@gmail.com",
        lastSync: new Date().toISOString(),
      };

      return {
        mode: "inline",
        user: persistUser(fallbackUser),
      };
    }

    throw error;
  }
};

export const disconnectGoogleAccount = async () => {
  try {
    const response = await apiClient.delete("/google/disconnect");
    return persistUser(normalizeUser(response.data));
  } catch (error) {
    if (isFallbackEligible(error)) {
      const fallbackUser = {
        ...readStoredUser(),
        googleConnected: false,
        googleEmail: "",
      };

      return persistUser(fallbackUser);
    }

    throw error;
  }
};

export const getGoogleProfile = async () => {
  try {
    const response = await apiClient.get("/google/profile");
    const profile = response.data?.profile || {};

    const nextUser = {
      ...readStoredUser(),
      googleConnected: true,
      googleEmail: profile.email || readStoredUser().googleEmail || "",
      googleProfilePicture: profile.picture || "",
      lastSync: new Date().toISOString(),
    };

    return persistUser(nextUser);
  } catch (error) {
    if (isUnauthenticated(error)) {
      return persistUser({
        ...readStoredUser(),
        googleConnected: false,
        googleEmail: "",
        googleProfilePicture: "",
      });
    }

    throw error;
  }
};

export { initialUser };
